import {
  foldToPair,
  normalizeClassification,
  type Classifications
} from "./classification";
import type { HierarchyLevel } from "./types";

// A realistic stadium scope catalog, used to seed the mock templates.
//
// Leaf systems are deliberately mixed: some items classify down to a
// Uniformat element, some to a MasterFormat section, and some end in a
// project-specific Custom label — so the matrix shows the full range.

type RawEntry = {
  hierarchy: HierarchyLevel[];
  description: string;
};

type CatalogEntry = {
  classification?: Classifications;
  description: string;
};

const uf = (code: string, title: string): HierarchyLevel => ({
  system: "UF",
  code,
  title
});
const mf = (code: string, title: string): HierarchyLevel => ({
  system: "MF",
  code,
  title
});
const custom = (title: string): HierarchyLevel => ({
  system: "CUSTOM",
  code: "",
  title
});

const A = [uf("A", "Substructure"), uf("A10", "Foundations")];
const B = [uf("B", "Shell")];
const C = [uf("C", "Interiors")];
const D = [uf("D", "Services")];

const RAW_CATALOG: RawEntry[] = [
  // --- substructure / concrete ---
  {
    hierarchy: [...A, mf("03 30 00", "Cast-in-Place Concrete")],
    description: "Spread footings and grade beams at the north stand."
  },
  {
    hierarchy: [...A, uf("A1010", "Standard Foundations")],
    description: "Mat foundation at west tower elevator/stair cores."
  },
  {
    hierarchy: [...A, mf("03 21 00", "Reinforcing Steel")],
    description: "Supply and install reinforcing steel for all foundations."
  },
  {
    hierarchy: [...A, mf("31 23 16", "Excavation"), custom("Bowl excavation")],
    description: "Mass excavation and haul-off for the seating bowl."
  },
  {
    hierarchy: [...A, mf("31 63 29", "Drilled Concrete Piers")],
    description: "Drilled piers supporting the roof canopy columns."
  },
  {
    hierarchy: [
      ...A,
      mf("07 10 00", "Dampproofing and Waterproofing"),
      custom("Below-grade waterproofing")
    ],
    description: "Below-grade waterproofing at service-level walls."
  },
  // --- shell / superstructure ---
  {
    hierarchy: [
      ...B,
      uf("B10", "Superstructure"),
      mf("05 12 00", "Structural Steel Framing")
    ],
    description: "Long-span canopy and upper-deck steel framing."
  },
  {
    hierarchy: [
      ...B,
      uf("B10", "Superstructure"),
      mf("03 41 00", "Precast Structural Concrete"),
      custom("Precast seating risers")
    ],
    description: "Precast concrete seating risers, all tiers."
  },
  {
    hierarchy: [
      ...B,
      uf("B10", "Superstructure"),
      mf("05 31 00", "Steel Decking")
    ],
    description: "Composite metal deck at concourse levels."
  },
  {
    hierarchy: [
      ...B,
      uf("B20", "Exterior Enclosure"),
      mf("04 22 00", "Concrete Unit Masonry")
    ],
    description: "CMU backup walls at concourse enclosure."
  },
  {
    hierarchy: [
      ...B,
      uf("B20", "Exterior Enclosure"),
      uf("B2020", "Exterior Windows")
    ],
    description: "Curtain wall at main entry and club lounges."
  },
  {
    hierarchy: [
      ...B,
      uf("B20", "Exterior Enclosure"),
      uf("B2010", "Exterior Walls")
    ],
    description: "Insulated metal wall panels at exterior facade."
  },
  {
    hierarchy: [
      ...B,
      uf("B20", "Exterior Enclosure"),
      uf("B2030", "Exterior Doors")
    ],
    description: "Hollow metal doors and frames at service areas."
  },
  {
    hierarchy: [...B, uf("B30", "Roofing"), mf("07 31 13", "Asphalt Shingles")],
    description: "Architectural asphalt shingles at sloped west-stand roofs."
  },
  {
    hierarchy: [...B, uf("B30", "Roofing"), uf("B3010", "Roof Coverings")],
    description: "PVC membrane roofing at low-slope concourse roofs."
  },
  {
    hierarchy: [
      ...B,
      uf("B30", "Roofing"),
      mf("07 62 00", "Sheet Metal Flashing and Trim"),
      custom("Roof edge metal")
    ],
    description: "Flashing, copings and gutters at all roof edges."
  },
  // --- interiors ---
  {
    hierarchy: [
      ...C,
      uf("C10", "Interior Construction"),
      uf("C1010", "Partitions")
    ],
    description: "Interior partitions at the suites level."
  },
  {
    hierarchy: [
      ...C,
      uf("C10", "Interior Construction"),
      mf("08 31 13", "Access Doors and Frames")
    ],
    description: "Access doors at mechanical and electrical rooms."
  },
  {
    hierarchy: [
      ...C,
      uf("C30", "Interior Finishes"),
      uf("C3030", "Ceiling Finishes")
    ],
    description: "Acoustical ceilings at club lounges and offices."
  },
  {
    hierarchy: [
      ...C,
      uf("C30", "Interior Finishes"),
      mf("09 65 13", "Resilient Base and Accessories")
    ],
    description: "Resilient flooring and base at back-of-house."
  },
  {
    hierarchy: [
      ...C,
      uf("C30", "Interior Finishes"),
      mf("09 91 23", "Interior Painting")
    ],
    description: "Interior painting throughout, all levels."
  },
  {
    hierarchy: [
      ...C,
      uf("C10", "Interior Construction"),
      custom("Premium suite millwork")
    ],
    description: "Custom millwork for premium suites."
  },
  {
    hierarchy: [
      ...C,
      uf("C30", "Interior Finishes"),
      uf("C3020", "Floor Finishes")
    ],
    description: "Ceramic tile at public restrooms."
  },
  {
    hierarchy: [...C, uf("C20", "Stairs"), uf("C2010", "Stair Construction")],
    description: "Metal pan stairs at all egress towers."
  },
  // --- services: plumbing / fire ---
  {
    hierarchy: [
      ...D,
      mf("22 05 23", "Floor Drains"),
      custom("Concourse floor drains")
    ],
    description: '4" floor drains with trap primers at concourse wet areas.'
  },
  {
    hierarchy: [
      ...D,
      uf("D20", "Plumbing"),
      uf("D2020", "Domestic Water Distribution")
    ],
    description: "Domestic water piping, risers and distribution."
  },
  {
    hierarchy: [...D, uf("D20", "Plumbing"), uf("D2030", "Sanitary Waste")],
    description: "Sanitary waste and vent piping, all levels."
  },
  {
    hierarchy: [...D, uf("D20", "Plumbing"), uf("D2010", "Plumbing Fixtures")],
    description: "Plumbing fixtures at public restrooms."
  },
  {
    hierarchy: [...D, mf("21 13 13", "Wet-Pipe Sprinkler Systems")],
    description: "Sprinkler coverage per NFPA 13."
  },
  {
    hierarchy: [
      ...D,
      mf("21 30 00", "Fire Pumps"),
      custom("Fire pump package")
    ],
    description: "Electric fire pump, jockey pump and controller."
  },
  // --- services: HVAC ---
  {
    hierarchy: [
      ...D,
      mf("23 73 13", "Modular Indoor Central-Station Air-Handling Units")
    ],
    description: "Air handling units serving concourse levels."
  },
  {
    hierarchy: [...D, uf("D30", "HVAC"), uf("D3040", "Distribution Systems")],
    description: "Sheet metal ductwork, all conditioned areas."
  },
  {
    hierarchy: [...D, mf("23 64 16", "Centrifugal Water Chillers")],
    description: "Water-cooled chillers at the central plant."
  },
  {
    hierarchy: [
      ...D,
      mf("23 21 13", "Hydronic Piping"),
      custom("Chilled/hot water piping")
    ],
    description: "Hydronic piping between plant and AHUs."
  },
  {
    hierarchy: [
      ...D,
      mf("23 09 00", "Instrumentation and Control for HVAC"),
      custom("Building automation")
    ],
    description: "BAS controls for HVAC systems."
  },
  // --- services: electrical / low voltage ---
  {
    hierarchy: [
      ...D,
      mf("26 51 00", "Interior Lighting"),
      custom("Bowl sports lighting")
    ],
    description: "LED sports lighting, bowl and concourse."
  },
  {
    hierarchy: [...D, mf("26 24 13", "Switchboards")],
    description: "Main electrical switchboards and distribution."
  },
  {
    hierarchy: [...D, mf("26 32 13", "Engine Generators")],
    description: "Diesel standby generator and paralleling gear."
  },
  {
    hierarchy: [
      ...D,
      mf("27 41 16", "Integrated Audio-Video Systems"),
      custom("Bowl AV and scoreboard")
    ],
    description: "Distributed audio and main video board."
  },
  {
    hierarchy: [...D, mf("28 31 00", "Fire Detection and Alarm")],
    description: "Addressable fire alarm and voice evacuation."
  },
  {
    hierarchy: [
      ...D,
      uf("D50", "Electrical"),
      uf("D5030", "Communications and Security")
    ],
    description: "Structured cabling and IDF build-outs."
  },
  // --- conveying / equipment / sitework ---
  {
    hierarchy: [
      ...D,
      uf("D10", "Conveying"),
      uf("D1010", "Elevators and Lifts")
    ],
    description: "Passenger and service elevators, west tower."
  },
  {
    hierarchy: [...C, mf("11 40 00", "Foodservice Equipment")],
    description: "Foodservice equipment at concession stands."
  },
  {
    hierarchy: [
      ...C,
      mf("12 66 13", "Telescoping Bleachers"),
      custom("Retractable seating")
    ],
    description: "Retractable seating at end zones."
  },
  {
    hierarchy: [...B, mf("10 14 00", "Signage"), custom("Wayfinding signage")],
    description: "Interior and exterior wayfinding signage."
  },
  {
    hierarchy: [uf("G", "Building Sitework"), mf("32 12 16", "Asphalt Paving")],
    description: "Asphalt paving at parking and service access."
  },
  {
    hierarchy: [
      uf("G", "Building Sitework"),
      mf("32 92 19", "Seeding"),
      custom("Field turf and landscaping")
    ],
    description: "Playing field turf system and site landscaping."
  },
  {
    hierarchy: [
      uf("G", "Building Sitework"),
      uf("G30", "Site Mechanical Utilities"),
      uf("G3010", "Water Supply")
    ],
    description: "Site water, storm and sanitary utilities."
  },
  {
    hierarchy: [
      ...B,
      uf("B20", "Exterior Enclosure"),
      custom("Facade access / BMU")
    ],
    description: "Building maintenance units for facade access."
  },
  {
    hierarchy: [...D, custom("Commissioning")],
    description: "Whole-building commissioning of MEP systems."
  }
];

// The literals are authored as ladders because that is how the taxonomy reads,
// and the LEAF is the classification each entry is meant to carry. Note this is
// deliberately not foldToPair's dual-standard merge: nearly every ladder here
// passes through a UniFormat ancestor on its way down, so that rule would
// collapse the whole catalog to UniFormat and leave the demo with no
// MasterFormat or custom items to exercise the "Other items" bucket.
// UniFormat leaves keep the UF chain as `path` so topOf / tree grouping still
// resolve to the division without re-deriving parents from the code string.
const leafOf = (levels: HierarchyLevel[]): Classifications | undefined => {
  for (let i = levels.length - 1; i >= 0; i -= 1) {
    const hit = normalizeClassification(levels[i]);
    if (!hit) continue;
    if (hit.system === "UF") {
      const ufChain = levels.filter((l) => l.system === "UF" && l.code.trim());
      return foldToPair(ufChain.length ? ufChain : [hit]);
    }
    return foldToPair([hit]);
  }
  return undefined;
};

export const DEMO_SCOPE_CATALOG: CatalogEntry[] = RAW_CATALOG.map((entry) => {
  const classification = leafOf(entry.hierarchy);
  return {
    ...(classification ? { classification } : {}),
    description: entry.description
  };
});
